import { useRef, useState } from 'react';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { F, mr } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import { analyzeDocument } from '../services/documentIngestion';
import { importCsvFile, importSpreadsheetFile } from '../utils/simpleCsvImporter';
import { validateTransactions } from '../utils/importValidation';
import { getAiConsent } from '../services/aiConsent';
import ImportReviewScreen from './ImportReviewScreen';
import AiConsentModal from '../components/AiConsentModal';
import ExchangeApiConnect from '../components/ExchangeApiConnect';

// ── CSV IMPORT SCREEN ────────────────────────────────────────────────────────

function CsvImport({ onImport, onBack, uid, existingTxs = [] }) {
  const { T } = useTheme();
  const { t } = useT();
  const { isWide } = useBreakpoint();
  const [dragging,         setDragging]         = useState(false);
  const [aiLoading,        setAiLoading]        = useState(false);
  const [consentPending,   setConsentPending]   = useState(null); // File waiting for consent
  const [aiError,          setAiError]          = useState(null);
  const [aiNote,           setAiNote]           = useState(null);
  const [currentFile,      setCurrentFile]      = useState(null);
  const [universalResult,  setUniversalResult]  = useState(null); // Review-Screen Daten
  const fileRef = useRef();

  // Wenn Review-Screen aktiv ist
  if (universalResult) {
    return (
      <ImportReviewScreen
        result={universalResult}
        fileName={currentFile?.name}
        uid={uid}
        existingTxs={existingTxs}
        onImport={async (cleanTxs, opts) => {
          setUniversalResult(null);
          await onImport(cleanTxs, opts);
        }}
        onCancel={() => setUniversalResult(null)}
      />
    );
  }

  const handleFile = async (file) => {
    if (!file) return;
    // Frueh abfangen: riesige Dateien wuerden beim Base64-Encoding (~+33% RAM)
    // den Tab zum Absturz bringen, bevor ueberhaupt ein Request rausgeht.
    // 10 MB deckt sich mit storage.rules; der Server cappt zusaetzlich (413).
    const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
    if (file.size > MAX_UPLOAD_BYTES) {
      setAiError('Datei zu groß (max. 10 MB). Bitte kleinere Datei wählen.');
      return;
    }
    setCurrentFile(file);
    setAiError(null); setAiNote(null); setUniversalResult(null);

    // CSV/TXT: einheitlich ueber importCsvFile aus simpleCsvImporter laufen
    // lassen. Frueher gab es einen ZWEITEN Parser-Stack in dieser Datei --
    // das fuehrte dazu, dass Fixes am Hauptparser am Upload vorbeigingen.
    const ext = file.name.split('.').pop().toLowerCase();
    if (['csv', 'txt'].includes(ext)) {
      try {
        const { exchange, txs, warnings } = await importCsvFile(file);
        if (!txs || txs.length === 0) {
          // Parser fand nichts -> KI-Fallback wie bisher
          handleUniversalImport(file);
          return;
        }
        setUniversalResult({
          transactions: validateTransactions(txs),
          exchange: exchange || null,
          warnings: warnings || [],
        });
      } catch (err) {
        console.warn('[CsvImport] importCsvFile failed, fallback to AI:', err);
        handleUniversalImport(file);
      }
    } else if (['xlsx', 'xls'].includes(ext)) {
      // Kunden-Excel: ZUERST lokal deterministisch lesen (SheetJS → CSV-Text →
      // gehärteter Parser mit unserem Typ-/Spaltenwissen). Das ist exakt,
      // kostenlos und ohne KI-Einwilligung. Nur wenn der Parser nichts
      // Brauchbares findet (unstrukturierte Tabelle), fällt es auf die KI zurück.
      try {
        const { exchange, txs, warnings } = await importSpreadsheetFile(file);
        if (!txs || txs.length === 0) {
          handleUniversalImport(file);
          return;
        }
        setUniversalResult({
          transactions: validateTransactions(txs),
          exchange: exchange || null,
          warnings: warnings || [],
        });
      } catch (err) {
        console.warn('[CsvImport] importSpreadsheetFile failed, fallback to AI:', err);
        handleUniversalImport(file);
      }
    } else {
      // Für PDF, Bilder: direkt Universal-KI starten
      handleUniversalImport(file);
    }
  };

  // Universal AI Import — 5-Phasen-Pipeline
  // DSGVO: Vor dem ersten KI-Upload Einwilligung einholen.
  const handleUniversalImport = async (file) => {
    const f = file || currentFile;
    if (!f) return;
    // Consent prüfen — wenn nicht vorhanden, Modal zeigen und Datei parken
    if (uid) {
      const consent = await getAiConsent(uid);
      if (!consent) {
        setConsentPending(f);
        return;
      }
    }
    setAiLoading(true); setAiError(null); setAiNote(null);
    try {
      const result = await analyzeDocument(f, { uid });
      if (!result.transactions?.length) {
        setAiError(t('misc_csv_err_no_tx'));
        return;
      }
      if (!result.requiresReview && result.overallConfidence >= 0.82) {
        // Hohe Konfidenz — direkt zur Bestätigung mit kurzer Preview
        setUniversalResult(result);
      } else {
        // Review-Screen für manuelle Prüfung
        setUniversalResult(result);
      }
    } catch (err) {
      setAiError(err.message || t('misc_csv_err_connection'));
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div style={{
      padding: isWide ? '20px 0 32px' : '20px 16px 100px',
      maxWidth: isWide ? 720 : 'none',
      margin: isWide ? '0 auto' : undefined,
    }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: T.text, fontFamily: F.family, marginBottom: 6 }}>{t('misc_csv_import_title')}</div>
      <div style={{ fontSize: 13, color: T.muted, fontFamily: F.family, marginBottom: 24 }}>
        {t('misc_csv_import_sub')}
      </div>

      {/* ── Upload-Zone ── */}
      <div
        onClick={() => !aiLoading && fileRef.current.click()}
        onDragOver={e => { e.preventDefault(); if (!aiLoading) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); if (!aiLoading) handleFile(e.dataTransfer.files[0]); }}
        style={{
          border: `2px dashed ${dragging ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.12)'}`,
          borderRadius: mr(16), padding: '32px 20px', textAlign: 'center',
          cursor: aiLoading ? 'default' : 'pointer',
          background: dragging ? 'rgba(255,255,255,0.06)' : T.glass,
          backdropFilter: T.glassBlur,
          WebkitBackdropFilter: T.glassBlur,
          marginBottom: 12,
          transition: 'all 0.2s ease',
          boxShadow: dragging ? '0 0 24px rgba(255,255,255,0.18)' : T.cardShadow,
        }}
      >
        {aiLoading ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
              <AiSpinner color={T.red} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text, fontFamily: F.family }}>{t('misc_csv_analyzing')}</div>
            <div style={{ fontSize: 12, color: T.muted, fontFamily: F.family, marginTop: 4 }}>{t('misc_csv_analyzing_sub')}</div>
          </div>
        ) : (
          <>
            <svg width="56" height="56" viewBox="0 0 64 64" fill="none" style={{ display: 'block', margin: '0 auto 12px' }} aria-hidden="true">
              {/* square.and.arrow.up — Tray neutral, NUR der Pfeil koralle. Kein Glow/Container. */}
              <path d="M22 34 v8 a3 3 0 0 0 3 3 h14 a3 3 0 0 0 3 -3 v-8" stroke={T.sub} strokeWidth="2.4" strokeLinecap="round" />
              <line x1="32" y1="20" x2="32" y2="39" stroke="#E4544A" strokeWidth="2.6" strokeLinecap="round" />
              <path d="M25 27 l7 -7 l7 7" stroke="#E4544A" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text, fontFamily: F.family, marginBottom: 4 }}>{t('misc_csv_drop_title')}</div>
            <div style={{ fontSize: 12, color: T.muted, fontFamily: F.family }}>{t('misc_csv_drop_sub')}</div>
          </>
        )}
        <input ref={fileRef} type="file" accept=".csv,.txt,.xlsx,.xls,.pdf,.jpg,.jpeg,.png,.webp" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
      </div>

      {/* ── AI-Fehler ── */}
      {aiError && (
        <div style={{ padding: '12px 16px', background: 'rgba(231,76,60,0.1)', border: `1px solid ${T.red}44`, borderRadius: mr(16), marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: T.red, fontFamily: F.family, fontWeight: 600 }}>⚠ {aiError}</div>
        </div>
      )}

      {/* ── AI-Hinweis (z.B. Partial-Import) ── */}
      {aiNote && (
        <div style={{ padding: '10px 14px', background: 'rgba(136,139,255,0.1)', border: '1px solid rgba(136,139,255,0.25)', borderRadius: mr(16), marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#888BFF', fontFamily: F.family }}>ℹ {aiNote}</div>
        </div>
      )}

      <button onClick={onBack} style={{
        width: '100%', padding: '13px', background: 'none', color: T.muted,
        border: `1px solid ${T.border}`, borderRadius: mr(16), fontFamily: F.family,
        fontSize: 14, fontWeight: 600, cursor: 'pointer',
      }}>{t('cancel')}</button>

      {/* DSGVO: Einwilligung vor erstem KI-Upload */}
      {consentPending && (
        <AiConsentModal
          uid={uid}
          onAccept={() => {
            const f = consentPending;
            setConsentPending(null);
            handleUniversalImport(f);
          }}
          onCancel={() => setConsentPending(null)}
        />
      )}
    </div>
  );
}

// Spinner für AI-Lade-Zustand (wiederverwendet CSS-Animation)
function AiSpinner({ color = '#e74c3c', size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ animation: 'spin 0.8s linear infinite' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <circle cx="16" cy="16" r="12" fill="none" stroke={color} strokeWidth="3" strokeOpacity="0.2" />
      <path d="M16 4 A12 12 0 0 1 28 16" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// ── API CONNECT — siehe components/ExchangeApiConnect.jsx ────────────────────
// Die Funktionalität ist dorthin ausgelagert. SettingsScreen.js delegiert nur.

export default function SettingsScreen({ mode, onImport, onBack, uid, existingTxs, onOpenPricing }) {
  if (mode === 'csv') return <CsvImport onImport={onImport} onBack={onBack} uid={uid} existingTxs={existingTxs} />;
  if (mode === 'api') return <ExchangeApiConnect onBack={onBack} onImport={onImport} onOpenPricing={onOpenPricing} />;
  return null;
}
